import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  SEARCH_TYPE,
  FIRST_NAME,
  LAST_NAME,
  SEARCH_CRITERIA,
  COMMUNITY_ID,
  USER_IDS,
  START_DATE,
  END_DATE,
  BOM_POSITION_ID,
} from "./consts";
import { getRequiredFields } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect, useState } from "react";
import { addBomFormValues, addBomSchema, bomSchema } from "./validations";
import { bomFormValues } from "./validations";
import { toast } from "@/components/Toast";
import {
  useAssignBulkBomUserMutation,
  useGetBomAssociateUserByCommunityId,
  useGetBOMPositions,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useAssignBOM = ({ onCancel }: { onCancel: () => void }) => {
  const [rowSelection, setRowSelection] = useState({});
  const [communityUsersList, setCommunityUsersList] = useState([]);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<bomFormValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [FIRST_NAME]: "",
      [LAST_NAME]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(bomSchema),
  });

  const addForm = useForm<addBomFormValues>({
    defaultValues: {
      [BOM_POSITION_ID]: "",
      [START_DATE]: "",
      [END_DATE]: "",
      [USER_IDS]: "",
      [COMMUNITY_ID]: "",
    },
    resolver: zodResolver(addBomSchema),
  });

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  // get property owners for the community
  const { data: communityUsers, isLoading: isCommunityUsersLoading } =
    useGetBomAssociateUserByCommunityId({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const columns = [
    {
      id: "select",
      header: ({ table }: { table: any }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }: { row: any }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "userName",
      header: "User ID",
      accessorFn: (row: any) => String(row.userName),
    },
    {
      accessorKey: "firstName",
      header: "First Name",
      accessorFn: (row: any) => String(row?.firstName || ""),
    },
    {
      accessorKey: "lastName",
      header: "Last Name",
      accessorFn: (row: any) => String(row?.lastName || ""),
    },
    {
      accessorKey: "positionName",
      header: "Existing Position",
      accessorFn: (row: any) => String(row?.positionName || ""),
    },
    {
      accessorKey: "email",
      header: "Email",
      accessorFn: (row: any) => String(row?.email || ""),
    },
    {
      accessorKey: "mobileNo",
      header: "Phone",
      accessorFn: (row: any) => String(row?.mobileNo || ""),
    },
  ];

  // to get all the positions for the community
  const { data: bomPositions, isLoading: isBOMPositionsLoading } =
    useGetBOMPositions({ communityId: communityDetail?.communityId as string });

  useEffect(() => {
    setCommunityUsersList(communityUsers?.data || []);
  }, [communityUsers]);

  const requiredFields = getRequiredFields(bomSchema);
  const requiredAddBomFields = getRequiredFields(addBomSchema);

  const onSubmit = (data: bomFormValues) => {
    let filteredUsers = [];
    if (data[SEARCH_TYPE] === "name") {
      filteredUsers = communityUsersList.filter(
        (user: any) =>
          user.firstName
            .toLowerCase()
            .includes(data?.[FIRST_NAME]?.toLowerCase()) ||
          user.lastName.toLowerCase().includes(data?.[LAST_NAME]?.toLowerCase())
      );
    } else {
      filteredUsers = communityUsersList.filter(
        (user: any) =>
          user.userName
            ?.toLowerCase()
            .includes(data?.[SEARCH_CRITERIA]?.toLowerCase()) ||
          user.email
            ?.toLowerCase()
            .includes(data?.[SEARCH_CRITERIA]?.toLowerCase()) ||
          user.mobileNo
            ?.toLowerCase()
            .includes(data?.[SEARCH_CRITERIA]?.toLowerCase())
      );
    }
    setCommunityUsersList(filteredUsers);
  };

  // assign bom to user

  const { mutate: assignBulkBom, isPending: isAssignPending } =
    useAssignBulkBomUserMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Community Info Added Successfully");
        queryClient.invalidateQueries({
          queryKey: ["bomUsersList", communityDetail?.communityId],
        });
        onCancel();
      },
    });

  const onSubmitAddBOM = (data: addBomFormValues) => {
    const userIds = Object.keys(rowSelection).map(
      (key) => communityUsers?.data[key]?.userId
    );
    assignBulkBom({
      data: {
        ...data,
        [COMMUNITY_ID]: communityDetail?.communityId,
        [USER_IDS]: userIds,
      },
    });
  };

  const onClear = () => {
    form.reset();
    setRowSelection({});
    setCommunityUsersList(communityUsers?.data || []);
  };

  const onAssignBOM = () => {
    if (Object.keys(rowSelection).length > 0) {
      addForm.reset();
      addForm.clearErrors();
      setOpen(true);
    } else {
      toast.error("Please select at least one user", {
        className: "!text-white !bg-red-500",
      });
    }
  };

  const onCloseDialog = () => {
    setOpen(false);
  };

  return {
    form,
    columns,
    onSubmit,
    requiredFields,
    rowSelection,
    setRowSelection,
    onClear,
    onAssignBOM,
    open,
    setOpen,
    requiredAddBomFields,
    addForm,
    onCloseDialog,
    onSubmitAddBOM,
    isAssignPending,
    communityUsers,
    isCommunityUsersLoading,
    bomPositions,
    isBOMPositionsLoading,
    communityUsersList,
  };
};

export default useAssignBOM;
